import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { TaskConstants } from "../../../../constants/workflow/TaskConstants";
import { SelectData, TaskPanelData } from "../../../../models";
import { searchOrganisationUsers } from "../../../../service/query";
import { Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";

interface UseTaskPanelRightProps {
  currentTask: TaskPanelData;
  setCurrentTask: Dispatch<SetStateAction<TaskPanelData>>;
}

interface UseTaskPanelRightReturnData {
  currentOrganisationUuid: string | undefined;
  assigneeList: SelectData;
  handleAssigneeSearch: (search: string) => void;
  handleSelectAssignee: (value: string) => void;
}
export const useTaskPanelRight = ({
  currentTask,
  setCurrentTask,
}: UseTaskPanelRightProps): UseTaskPanelRightReturnData => {
  const { currentOrganisationUuid } = useAuth();
  const [assigneeList, setAssigneeList] = useState<SelectData>([]);

  const handleAssigneeSearch = async (search: string): Promise<void> => {
    if (!currentOrganisationUuid) return;

    const response = await searchOrganisationUsers({
      organisationUuid: currentOrganisationUuid,
      paging: { limit: 50, beforeCursor: "", afterCursor: "" },
      sort: [{ key: "user.fullName" as "results.user.fullName", direction: "asc" }],
      filter: {
        results: {
          active: { operator: "eq", value: true },
          user: { fullName: { operator: "startsWith", value: search } },
        },
      },
    });

    if (response.status === Status.Success && response.data?.results) {
      const list = response.data.results.flatMap((el) =>
        el.user.uuid && el.user.fullName ? { key: el.user.uuid, value: el.user.fullName } : []
      );
      setAssigneeList([{ key: "unassigned", value: "Unassigned" }, ...list]);
    }
  };

  const handleSelectAssignee = (value: string): void => {
    let newStatus;
    if (value === "unassigned") {
      newStatus = TaskConstants.TASK_STATUS_UNASSIGNED;
    } else if (currentTask.status === TaskConstants.TASK_STATUS_UNASSIGNED) {
      newStatus = TaskConstants.TASK_STATUS_TO_DO;
    } else {
      newStatus = currentTask.status;
    }

    setCurrentTask((prevTask) => ({
      ...prevTask,
      assignedToUserUuid: value,
      status: newStatus,
    }));
  };

  useEffect(() => {
    if (currentTask.assignedToUserName) handleAssigneeSearch(currentTask.assignedToUserName);
    handleAssigneeSearch(currentTask.assignedToUserName ?? "");
  }, [currentTask.assignedToUserName]);

  return {
    currentOrganisationUuid,
    assigneeList,
    handleAssigneeSearch,
    handleSelectAssignee,
  };
};
